<!-- eslint-disable @intlify/vue-i18n/no-unused-keys -->
<i18n lang="json">
{
  "fr": {
    "op-eq": "Est",
    "op-neq": "N'est pas",
    "op-sw": "Commence par",
    "op-ew": "Se termine par",
    "op-ct": "Contient",
    "op-nct": "Ne contient pas",
    "op-lt": "Plus petit que",
    "op-le": "Plus petit ou égal à",
    "op-gt": "Plus grand que",
    "op-ge": "Plus grand ou égal à",
    "op-yes": "Oui",
    "op-no": "Non",
    "subject": "Sujet",
    "rule": "Règle",
    "quantity": "Quantité",
    "value": "Valeur",
    "select": "Sélectionnez une option",
    "edit-condition": "Modifier la condition",
    "delete-condition": "Supprimer la condition"
  },
  "en": {
    "op-eq": "Is",
    "op-neq": "Is not",
    "op-sw": "Starts with",
    "op-ew": "Ends with",
    "op-ct": "Contains",
    "op-nct": "Does not contain",
    "op-lt": "Lesser than",
    "op-le": "Lesser or equal to",
    "op-gt": "Greater than",
    "op-ge": "Greater or equal to",
    "op-yes": "Yes",
    "op-no": "No",
    "subject": "Subject",
    "rule": "Rule",
    "quantity": "Quantity",
    "value": "Value",
    "select": "Select option",
    "edit-condition": "Edit condition",
    "delete-condition": "Delete condition"
  }
}
</i18n>

<template>
  <div class="relative ml-4 flex items-center gap-2 rounded-md bg-primary-wash pl-4 pr-1 dark:bg-grey-800">
    <PfIcon v-if="showHandle" class="handle cursor-move opacity-60" size="sm" :icon="ICON_DRAG_HANDLE" />
    <p class="mb-0 inline-flex flex-wrap text-sm gap-x-2">
      <span class="font-medium">{{ currentProperty?.label }}</span>
      <span v-if="props.modelValue.op" class="font-bold lowercase text-primary-main dark:text-primary-main">
        {{ t(`op-${props.modelValue.op}`) }}
      </span>
      <span>{{ currentPropertyValue }}</span>
    </p>
    <PfButtonGroup class="ml-auto mr-0" button-size="sm" tooltip-position="left" :items="getMenuItems()" />
    <span class="absolute -left-4 top-1/2 h-full w-px -translate-y-full bg-grey-700"></span>
    <span class="absolute -left-4 top-1/2 h-px w-4 bg-grey-700"></span>
  </div>

  <UiDialogModal ref="modal" :title="t('edit-condition')">
    <template #description>
      <PfFormLayout class="text-left">
        <PfFormSection>
          <PfFormInputSelect
            v-if="propertyOptions"
            class="shrink-0"
            :label="t('subject')"
            :options="propertyOptions"
            :model-value="props.modelValue.prop"
            placeholder=" "
            @update:model-value="(prop: string) => onUpdate({ prop })" />

          <PfFormInputSelect
            v-if="currentProperty"
            class="shrink-0"
            :label="t('rule')"
            :options="operatorOptions"
            :model-value="props.modelValue.op"
            @update:model-value="(op: PropertyConditionOperator) => onUpdate({ op })" />

          <PfFormInputSelect
            v-if="currentProperty?.type === 'enum'"
            class="grow"
            :label="t('select')"
            :options="currentProperty.values"
            :model-value="props.modelValue.value"
            @update:model-value="(value: string) => onUpdate({ value })" />

          <PfFormInputText
            v-if="currentProperty && ['string', 'number'].includes(currentProperty.type)"
            class="grow"
            :input-type="currentProperty.type === 'number' ? 'number' : 'text'"
            :label="currentProperty.type === 'number' ? t('quantity') : t('value')"
            :value="props.modelValue.value"
            @input="(ev: Event) => onUpdate({ value: (ev.target as HTMLInputElement).value })" />
        </PfFormSection>
      </PfFormLayout>
    </template>
  </UiDialogModal>
</template>

<script lang="ts" setup>
import type { PropertyCondition, PropertyConditionOperator, ConditionEditorProperty } from "@/lib/helpers/condition";
import { computed, watchEffect, ref, nextTick, watch } from "vue";
import { useI18n } from "vue-i18n";
import ICON_EDIT from "@/lib/icons/edit.json";
import ICON_TRASH from "@/lib/icons/trash.json";
import ICON_DRAG_HANDLE from "@/lib/icons/burger.json";

const { t } = useI18n();
const modal = ref();

const emit = defineEmits<{
  // eslint-disable-next-line no-unused-vars
  (e: "update:modelValue", condition: PropertyCondition): void;
  // eslint-disable-next-line no-unused-vars
  (e: "delete"): void;
}>();

const props = defineProps<{
  modelValue: PropertyCondition;
  properties?: ConditionEditorProperty[];
  showHandle?: boolean;
}>();

defineExpose({ openModal });

const propertyOptions = computed(() => {
  if (!props.properties) return null;
  return props.properties.map((x) => ({
    label: x.label,
    value: x.prop
  }));
});

const currentProperty = computed(() => {
  if (props.properties) {
    return props.properties.find((x) => x.prop === props.modelValue.prop);
  } else {
    return null;
  }
});

const propertyValueFromEnum = computed(() => {
  if (currentProperty.value?.type !== "enum") return "";
  if (!currentProperty.value?.values) return "";
  const currentValue = currentProperty.value?.values.find((x) => x.value === props.modelValue.value);
  if (!currentValue) return "";
  return currentValue.label;
});

const currentPropertyValue = computed(() =>
  currentProperty.value?.type === "enum" ? propertyValueFromEnum.value : props.modelValue.value
);

const operators = computed<PropertyConditionOperator[]>(() => {
  switch (currentProperty.value?.type) {
    case "enum":
      return ["eq", "neq"];
    case "string":
      return ["eq", "neq", "sw", "ew", "ct", "nct"];
    case "number":
      return ["eq", "neq", "lt", "le", "gt", "ge"];
    case "yesNo":
      return ["yes", "no"];
    default:
      return [];
  }
});

const operatorOptions = computed(() => operators.value.map((x) => ({ value: x, label: t(`op-${x}`) })));

async function openModal() {
  await nextTick();
  modal.value?.openModal();
}

function onUpdate(newValues: Partial<PropertyCondition>) {
  if (currentProperty.value?.type === "number" && "value" in newValues && typeof newValues.value === "string") {
    var parsedValue = parseFloat(newValues.value);
    if (!isNaN(parsedValue)) {
      newValues.value = parsedValue;
    }
  }
  // Automatically open modal when property is added
  if (props.modelValue.value) {
    openModal();
  }

  emit("update:modelValue", { ...props.modelValue, ...newValues });
}

// Sélectionner la première option des dropdown par défaut
watchEffect(() => {
  if (propertyOptions.value) {
    selectFirstOption("prop", propertyOptions.value);
  }

  if (operatorOptions.value) {
    selectFirstOption("op", operatorOptions.value);
  }

  if (currentProperty.value?.type === "enum") {
    selectFirstOption("value", currentProperty.value.values);
  }
});

function selectFirstOption(property: keyof PropertyCondition, options: { value: string }[]) {
  if (options.length === 0) return;

  const currentOption = options.find((x) => x.value === props.modelValue[property]);
  if (currentOption === undefined) {
    onUpdate({ [property]: options[0].value });
  }
}

// Lorsque le type de propriété change, supprimer la valeur
watch(
  () => currentProperty.value?.type,
  () => onUpdate({ value: undefined })
);

function getMenuItems() {
  return [
    {
      label: t("edit-condition"),
      icon: ICON_EDIT,
      onClick() {
        openModal();
      }
    },
    {
      label: t("delete-condition"),
      icon: ICON_TRASH,
      onClick() {
        emit("delete");
      }
    }
  ];
}
</script>
